import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createForumPost,
  createForumTopic,
  fetchForumCategories,
  fetchForumTopic,
  fetchForumTopics,
  moderateForumPost,
  moderateForumTopic,
  voteForumPoll,
} from "../../api/forumService";
import { fetchCategories } from "../../api/categoryService";
import {
  createModeratorApplication,
  fetchModeratorApplications,
  voteModeratorApplication,
} from "../../api/communityService";
import { useAuth } from "../../context/AuthContext";
import { readSharedAuthSnapshot } from "../../utils/sharedAuth";

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function getApiError(error, fallback) {
  return error?.response?.data?.error || fallback;
}

export default function Forum() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, logout } = useAuth();

  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topicLoading, setTopicLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [newTopic, setNewTopic] = useState({
    category_slug: "",
    title: "",
    body: "",
    has_poll: false,
  });
  const [replyBody, setReplyBody] = useState("");

  const [gameCategories, setGameCategories] = useState([]);
  const [applicationMotivation, setApplicationMotivation] = useState("");
  const [applicationCategoryId, setApplicationCategoryId] = useState("");
  const [applicationError, setApplicationError] = useState("");
  const [applicationMessage, setApplicationMessage] = useState("");

  const [moderatorApplications, setModeratorApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applicationsError, setApplicationsError] = useState("");

  const selectedCategory = searchParams.get("categorie") || "";
  const selectedTopicId = searchParams.get("sujet") || "";
  const canModerate = Boolean(user?.is_staff || user?.is_superuser || user?.is_moderator);
  const hasActiveAuthToken = () => Boolean(readSharedAuthSnapshot().access);
  const canUseAuthenticatedActions = Boolean(isAuthenticated && user && hasActiveAuthToken());

  const requireActiveSession = () => {
    if (user && !hasActiveAuthToken()) {
      logout();
    }
    navigate("/authentification#connexion");
  };

  const loadForum = useCallback(async (signal) => {
    setLoading(true);
    try {
      const [categoryData, topicData] = await Promise.all([
        fetchForumCategories(signal),
        fetchForumTopics(selectedCategory ? { category: selectedCategory } : {}, signal),
      ]);
      const categoryRows = Array.isArray(categoryData.categories) ? categoryData.categories : [];
      setCategories(categoryRows);
      setTopics(Array.isArray(topicData.topics) ? topicData.topics : []);
      setNewTopic((current) => ({
        ...current,
        category_slug: current.category_slug || selectedCategory || categoryRows[0]?.slug || "",
      }));
      setError("");
    } catch {
      setError("Impossible de charger le forum.");
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  const loadTopic = useCallback(async (topicId, signal) => {
    if (!topicId) {
      setSelectedTopic(null);
      setPosts([]);
      return;
    }
    setTopicLoading(true);
    try {
      const data = await fetchForumTopic(topicId, signal);
      setSelectedTopic(data.topic);
      setPosts(Array.isArray(data.posts) ? data.posts : []);
      setError("");
    } catch {
      setError("Impossible de charger ce sujet.");
      setSelectedTopic(null);
      setPosts([]);
    } finally {
      setTopicLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadForum(controller.signal);
    return () => controller.abort();
  }, [loadForum]);

  useEffect(() => {
    const controller = new AbortController();
    loadTopic(selectedTopicId, controller.signal);
    return () => controller.abort();
  }, [selectedTopicId, loadTopic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadApplicationData = async () => {
      try {
        const categoryData = await fetchCategories(controller.signal);
        const categoryRows = Array.isArray(categoryData) ? categoryData : [];
        setGameCategories(categoryRows);
        setApplicationCategoryId((current) => current || categoryRows[0]?.id || "");
      } catch {
        setError("Impossible de charger les categories de jeu.");
      }
    };

    loadApplicationData();
    return () => controller.abort();
  }, []);

  const loadModeratorApplications = useCallback(async (signal) => {
    setApplicationsLoading(true);
    setApplicationsError("");
    try {
      const data = await fetchModeratorApplications(signal);
      setModeratorApplications(Array.isArray(data?.applications) ? data.applications : Array.isArray(data) ? data : []);
    } catch (loadError) {
      if (loadError?.name !== "CanceledError" && loadError?.code !== "ERR_CANCELED") {
        setApplicationsError(getApiError(loadError, "Impossible de charger les demandes de moderation."));
      }
    } finally {
      setApplicationsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadModeratorApplications(controller.signal);
    return () => controller.abort();
  }, [loadModeratorApplications]);

  const activeCategory = useMemo(
    () => categories.find((category) => category.slug === selectedCategory),
    [categories, selectedCategory],
  );

  const filteredTopics = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return topics;
    return topics.filter((topic) => {
      const title = String(topic.title || "").toLowerCase();
      const author = String(topic.author?.username || "").toLowerCase();
      const category = String(topic.category?.name || "").toLowerCase();
      return title.includes(query) || author.includes(query) || category.includes(query);
    });
  }, [searchTerm, topics]);

  const selectCategory = (slug) => {
    const params = {};
    if (slug) params.categorie = slug;
    setSearchParams(params);
  };

  const selectTopic = (topicId) => {
    const params = {};
    if (selectedCategory) params.categorie = selectedCategory;
    params.sujet = topicId;
    setSearchParams(params);
  };

  const submitTopic = async (event) => {
    event.preventDefault();
    if (!canUseAuthenticatedActions) {
      requireActiveSession();
      return;
    }
    setError("");
    setMessage("");
    try {
      const data = await createForumTopic(newTopic);
      setMessage("Sujet cree.");
      setNewTopic((current) => ({ ...current, title: "", body: "", has_poll: false }));
      await loadForum();
      selectTopic(data.topic.id);
    } catch (submitError) {
      setError(getApiError(submitError, "Impossible de creer le sujet."));
    }
  };

  const submitReply = async (event) => {
    event.preventDefault();
    if (!canUseAuthenticatedActions) {
      requireActiveSession();
      return;
    }
    if (!selectedTopic) return;
    setError("");
    setMessage("");
    try {
      await createForumPost(selectedTopic.id, replyBody);
      setReplyBody("");
      setMessage("Réponse ajoutee.");
      await loadTopic(selectedTopic.id);
      await loadForum();
    } catch (submitError) {
      if (submitError?.response?.status === 401) {
        logout();
        setError("Session expiree. Reconnecte-toi pour repondre au forum.");
        navigate("/authentification#connexion");
        return;
      }
      setError(getApiError(submitError, "Impossible d'ajouter la réponse."));
    }
  };

  const submitPollVote = async (value) => {
    if (!canUseAuthenticatedActions) {
      requireActiveSession();
      return;
    }
    if (!selectedTopic?.id) return;
    setError("");
    setMessage("");
    try {
      const data = await voteForumPoll(selectedTopic.id, value);
      setSelectedTopic(data.topic);
      setTopics((current) =>
        current.map((topic) =>
          topic.id === data.topic.id
            ? {
                ...topic,
                has_poll: data.topic.has_poll,
                poll: data.topic.poll,
                updated_at: data.topic.updated_at,
              }
            : topic,
        ),
      );
      setMessage("Vote enregistre.");
    } catch (voteError) {
      if (voteError?.response?.status === 401) {
        logout();
        setError("Session expiree. Reconnecte-toi pour voter.");
        navigate("/authentification#connexion");
        return;
      }
      setError(getApiError(voteError, "Impossible d'enregistrer le vote."));
    }
  };

  const submitApplication = async (event) => {
    event.preventDefault();
    if (!canUseAuthenticatedActions) {
      requireActiveSession();
      return;
    }
    setApplicationError("");
    setApplicationMessage("");
    try {
      const data = await createModeratorApplication({
        motivation: applicationMotivation,
        categoryIds: applicationCategoryId ? [applicationCategoryId] : [],
      });
      setApplicationMotivation("");
      setApplicationMessage(data?.application ? "Candidature moderateur envoyee." : "Candidature moderateur envoyee.");
    } catch (applicationError) {
      setApplicationError(getApiError(applicationError, "Impossible d'envoyer la candidature."));
    }
  };

  const submitApplicationVote = async (applicationId, value) => {
    if (!canUseAuthenticatedActions) {
      requireActiveSession();
      return;
    }
    setApplicationsError("");
    try {
      const data = await voteModeratorApplication(applicationId, value);
      if (data?.application) {
        setModeratorApplications((current) =>
          current.map((application) =>
            application.id === data.application.id ? data.application : application,
          ),
        );
      } else {
        await loadModeratorApplications();
      }
    } catch (voteError) {
      if (voteError?.response?.status === 401) {
        logout();
        setApplicationsError("Session expiree. Reconnecte-toi pour voter.");
        navigate("/authentification#connexion");
        return;
      }
      setApplicationsError(getApiError(voteError, "Impossible d'enregistrer le vote."));
    }
  };

  const moderateTopic = async (action) => {
    if (!selectedTopic) return;
    setError("");
    try {
      const data = await moderateForumTopic(selectedTopic.id, action);
      setSelectedTopic(data.topic);
      await loadForum();
    } catch (moderationError) {
      setError(getApiError(moderationError, "Action de moderation refusee."));
    }
  };

  const moderatePost = async (postId, action) => {
    setError("");
    try {
      await moderateForumPost(postId, action);
      await loadTopic(selectedTopic.id);
    } catch (moderationError) {
      setError(getApiError(moderationError, "Action de moderation refusee."));
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(95,181,111,0.16),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(47,157,226,0.14),_transparent_30%),linear-gradient(180deg,_#fffdf5_0%,_#effcf6_45%,_#e9f7ff_100%)] px-4 py-10 text-pixy-ink sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-screen-2xl flex-col gap-8">
        <header className="rounded-[36px] border border-white/80 bg-white/80 p-6 shadow-[0_24px_80px_rgba(47,157,226,0.12)] backdrop-blur lg:p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-pixy-green">
            Forum PixyQuiz
          </p>
          <div className="mt-3 grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <h1 className="text-4xl font-black tracking-tight text-pixy-ink sm:text-5xl">
                Forum
              </h1>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                Retrouve les actualités PixyQuiz, les discussions de la communauté
                et les candidatures modérateurs dans une interface plus lisible.
              </p>
            </div>
            <div className="rounded-3xl border border-sky-100 bg-pixy-sky/70 px-5 py-4 text-sm text-slate-600">
              <span className="block text-2xl font-black text-pixy-blue">{topics.length}</span>
              Sujets ouverts
            </div>
          </div>
        </header>

        {(message || error) && (
          <div
            className={`rounded-3xl border px-5 py-4 text-sm shadow-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}
          >
            {error || message}
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="rounded-[32px] border border-emerald-100 bg-white/85 p-5 shadow-[0_18px_60px_rgba(47,157,226,0.10)] backdrop-blur xl:sticky xl:top-6 xl:self-start">
            <h2 className="text-sm font-black uppercase tracking-[0.22em] text-pixy-green">
              Navigation
            </h2>
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Rechercher un sujet..."
              className="mt-5 w-full rounded-2xl border border-sky-100 bg-white px-4 py-3 text-sm text-pixy-ink outline-none placeholder:text-slate-400 focus:border-pixy-blue"
            />
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => selectCategory("")}
                className={`rounded-2xl border px-4 py-3 text-left transition ${
                  !selectedCategory
                    ? "border-pixy-blue/40 bg-pixy-sky text-pixy-ink"
                    : "border-emerald-100 bg-white/80 text-slate-600 hover:border-pixy-blue/30"
                }`}
              >
                <span className="block font-bold">Tous les sujets</span>
                <span className="mt-1 block text-xs text-slate-500">{topics.length} sujets</span>
              </button>
              {categories.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  onClick={() => selectCategory(category.slug)}
                  className={`rounded-2xl border px-4 py-3 text-left transition ${
                    selectedCategory === category.slug
                      ? "border-pixy-blue/40 bg-pixy-sky text-pixy-ink"
                      : "border-emerald-100 bg-white/80 text-slate-600 hover:border-pixy-blue/30"
                  }`}
                >
                  <span className="block font-bold">{category.name}</span>
                  <span className="mt-1 block text-xs text-slate-500">
                    {category.topic_count} sujets
                  </span>
                </button>
              ))}
            </div>
          </aside>

          <main className="grid gap-6 2xl:grid-cols-[minmax(360px,0.9fr)_minmax(0,1.1fr)]">
            <section className="rounded-[32px] border border-sky-100 bg-white/85 p-5 shadow-[0_18px_60px_rgba(47,157,226,0.10)] backdrop-blur">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-pixy-blue">
                    {activeCategory?.name || "Tous les sujets"}
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-pixy-ink">Sujets</h2>
                </div>
                {loading && <span className="text-xs text-slate-500">Chargement...</span>}
              </div>

              <div className="mt-5 flex max-h-[720px] flex-col gap-3 overflow-y-auto pr-1">
                {filteredTopics.map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => selectTopic(topic.id)}
                    className={`rounded-3xl border p-4 text-left shadow-sm transition ${
                      String(topic.id) === String(selectedTopicId)
                        ? "border-pixy-blue/45 bg-pixy-sky"
                        : "border-emerald-100 bg-white/85 hover:-translate-y-0.5 hover:border-pixy-blue/30"
                    }`}
                  >
                    <span className="block text-base font-black text-pixy-ink">
                      {topic.title}
                    </span>
                    <span className="mt-2 block text-xs text-slate-500">
                      {topic.category?.name} - {topic.author?.username} - {formatDate(topic.updated_at)}
                    </span>
                    <span className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                      <span className="rounded-full border border-emerald-100 bg-pixy-mist px-3 py-1">
                        {topic.reply_count} réponses
                      </span>
                      {topic.is_closed && (
                        <span className="rounded-full border border-amber-100 bg-pixy-cream px-3 py-1">
                          Ferme
                        </span>
                      )}
                      {topic.is_hidden && (
                        <span className="rounded-full border border-rose-100 bg-rose-50 px-3 py-1 text-rose-700">
                          Masque
                        </span>
                      )}
                    </span>
                  </button>
                ))}
                {!loading && filteredTopics.length === 0 && (
                  <div className="rounded-3xl border border-dashed border-sky-200 bg-white/70 p-6 text-sm text-slate-500">
                    Aucun sujet trouvé.
                  </div>
                )}
              </div>

              {canUseAuthenticatedActions ? (
                <form onSubmit={submitTopic} className="mt-6 rounded-3xl border border-emerald-100 bg-pixy-mist/80 p-5">
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-pixy-green">
                    Nouveau sujet
                  </h3>
                  <select
                    value={newTopic.category_slug}
                    onChange={(event) => setNewTopic((current) => ({ ...current, category_slug: event.target.value }))}
                    className="mt-4 w-full rounded-2xl border border-emerald-100 bg-white px-4 py-3 text-sm text-pixy-ink outline-none focus:border-pixy-green"
                  >
                    {categories.map((category) => (
                      <option key={category.slug} value={category.slug}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <input
                    value={newTopic.title}
                    onChange={(event) => setNewTopic((current) => ({ ...current, title: event.target.value }))}
                    placeholder="Titre du sujet"
                    className="mt-3 w-full rounded-2xl border border-emerald-100 bg-white px-4 py-3 text-sm text-pixy-ink outline-none placeholder:text-slate-400 focus:border-pixy-green"
                    maxLength={140}
                  />
                  <textarea
                    value={newTopic.body}
                    onChange={(event) => setNewTopic((current) => ({ ...current, body: event.target.value }))}
                    placeholder="Message"
                    rows={5}
                    className="mt-3 w-full resize-none rounded-2xl border border-emerald-100 bg-white px-4 py-3 text-sm text-pixy-ink outline-none placeholder:text-slate-400 focus:border-pixy-green"
                  />
                  <label className="mt-3 flex items-start gap-3 rounded-2xl border border-emerald-100 bg-white/80 px-4 py-3 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={newTopic.has_poll}
                      onChange={(event) =>
                        setNewTopic((current) => ({
                          ...current,
                          has_poll: event.target.checked,
                        }))
                      }
                      className="mt-1 h-4 w-4 rounded border-emerald-200 text-pixy-green focus:ring-pixy-green"
                    />
                    <span>
                      <span className="block font-black text-pixy-ink">
                        Associer un sondage Oui / Non
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-500">
                        Chaque joueur peut voter une seule fois. Le vote ne peut pas être modifié.
                      </span>
                    </span>
                  </label>
                  <button
                    type="submit"
                    className="mt-3 rounded-full bg-gradient-to-r from-pixy-green via-pixy-blue to-sky-500 px-5 py-3 text-sm font-black text-white shadow-sm"
                  >
                    Créer le sujet
                  </button>
                </form>
              ) : (
                <div className="mt-6 rounded-3xl border border-sky-100 bg-pixy-sky/70 p-5">
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-pixy-blue">
                    Participer au forum
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Connectez-vous pour creer un sujet ou repondre.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/authentification#connexion")}
                    className="mt-4 rounded-full bg-pixy-blue px-5 py-3 text-sm font-black text-white"
                  >
                    Connexion / Inscription
                  </button>
                </div>
              )}
            </section>

            <section className="rounded-[32px] border border-emerald-100 bg-white/85 p-5 shadow-[0_18px_60px_rgba(47,157,226,0.10)] backdrop-blur">
              {!selectedTopic && (
                <div className="flex min-h-[360px] items-center justify-center rounded-3xl border border-dashed border-sky-200 bg-white/70 p-8 text-center text-sm text-slate-500">
                  Sélectionne un sujet pour lire la discussion.
                </div>
              )}
              {topicLoading && <div className="text-sm text-slate-500">Chargement du sujet...</div>}
              {selectedTopic && !topicLoading && (
                <article>
                  <div className="border-b border-emerald-100 pb-5">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span>{selectedTopic.category?.name}</span>
                      <span>par {selectedTopic.author?.username}</span>
                      <span>{formatDate(selectedTopic.created_at)}</span>
                    </div>
                    <h2 className="mt-3 text-3xl font-black text-pixy-ink">{selectedTopic.title}</h2>
                    <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-700">
                      {selectedTopic.body}
                    </p>
                    {selectedTopic.poll && (
                      <div className="mt-5 rounded-3xl border border-emerald-100 bg-pixy-mist/80 p-5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.22em] text-pixy-green">
                              Sondage
                            </p>
                            <h3 className="mt-2 text-xl font-black text-pixy-ink">
                              Oui ou non ?
                            </h3>
                            <p className="mt-1 text-xs text-slate-500">
                              {selectedTopic.poll.total_count} vote
                              {selectedTopic.poll.total_count > 1 ? "s" : ""} au total.
                            </p>
                          </div>
                          {selectedTopic.poll.user_vote && (
                            <span className="rounded-full border border-emerald-100 bg-white px-4 py-2 text-xs font-black text-pixy-green">
                              Votre vote est déjà pris en compte.
                            </span>
                          )}
                        </div>

                        {[
                          ["yes", "Oui", selectedTopic.poll.yes_count],
                          ["no", "Non", selectedTopic.poll.no_count],
                        ].map(([value, label, count]) => {
                          const total = selectedTopic.poll.total_count || 0;
                          const percent = total ? Math.round((count / total) * 100) : 0;
                          const isUserVote = selectedTopic.poll.user_vote === value;
                          return (
                            <div key={value} className="mt-4">
                              <div className="flex items-center justify-between gap-3 text-sm">
                                <span className="font-black text-pixy-ink">
                                  {label}
                                  {isUserVote ? " - votre vote" : ""}
                                </span>
                                <span className="font-bold text-slate-600">
                                  {count} vote{count > 1 ? "s" : ""} - {percent}%
                                </span>
                              </div>
                              <div className="mt-2 h-3 overflow-hidden rounded-full bg-white">
                                <div
                                  className={`h-full rounded-full ${
                                    value === "yes" ? "bg-pixy-green" : "bg-pixy-blue"
                                  }`}
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}

                        {selectedTopic.poll.can_vote ? (
                          <div className="mt-5 flex flex-wrap gap-3">
                            <button
                              type="button"
                              onClick={() => submitPollVote("yes")}
                              className="rounded-full bg-pixy-green px-5 py-3 text-sm font-black text-white shadow-sm"
                            >
                              Voter Oui
                            </button>
                            <button
                              type="button"
                              onClick={() => submitPollVote("no")}
                              className="rounded-full bg-pixy-blue px-5 py-3 text-sm font-black text-white shadow-sm"
                            >
                              Voter Non
                            </button>
                          </div>
                        ) : !canUseAuthenticatedActions ? (
                          <div className="mt-5 rounded-2xl border border-sky-100 bg-white/80 p-4 text-sm text-slate-600">
                            Connectez-vous pour voter.
                            <button
                              type="button"
                              onClick={() => navigate("/authentification#connexion")}
                              className="ml-0 mt-3 block rounded-full bg-pixy-blue px-5 py-3 text-sm font-black text-white sm:ml-3 sm:mt-0 sm:inline-block"
                            >
                              Connexion / Inscription
                            </button>
                          </div>
                        ) : selectedTopic.is_closed ? (
                          <p className="mt-5 text-sm font-bold text-slate-500">
                            Ce sujet est ferme, le sondage ne prend plus de votes.
                          </p>
                        ) : null}
                      </div>
                    )}
                    {canModerate && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button type="button" onClick={() => moderateTopic(selectedTopic.is_closed ? "reopen" : "close")} className="rounded-full border border-sky-100 bg-white px-4 py-2 text-xs font-bold text-pixy-blue">
                          {selectedTopic.is_closed ? "Rouvrir" : "Fermer"}
                        </button>
                        <button type="button" onClick={() => moderateTopic(selectedTopic.is_hidden ? "unhide" : "hide")} className="rounded-full border border-rose-100 bg-rose-50 px-4 py-2 text-xs font-bold text-rose-700">
                          {selectedTopic.is_hidden ? "Afficher" : "Masquer"}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-5 flex flex-col gap-4">
                    {posts.map((post) => (
                      <div key={post.id} className="rounded-3xl border border-sky-100 bg-white/80 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                          <span>{post.author?.username}</span>
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                        <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-slate-700">
                          {post.body}
                        </p>
                        {canModerate && (
                          <button
                            type="button"
                            onClick={() => moderatePost(post.id, post.is_hidden ? "unhide" : "hide")}
                            className="mt-3 rounded-full border border-rose-100 bg-rose-50 px-4 py-2 text-xs font-bold text-rose-700"
                          >
                            {post.is_hidden ? "Afficher la réponse" : "Masquer la réponse"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {canUseAuthenticatedActions ? (
                    <form onSubmit={submitReply} className="mt-6">
                      <textarea
                        value={replyBody}
                        onChange={(event) => setReplyBody(event.target.value)}
                        disabled={selectedTopic.is_closed}
                        placeholder={selectedTopic.is_closed ? "Sujet est fermé" : "Ta réponse"}
                        rows={4}
                        className="w-full resize-none rounded-2xl border border-sky-100 bg-white px-4 py-3 text-sm text-pixy-ink outline-none placeholder:text-slate-400 focus:border-pixy-blue disabled:cursor-not-allowed disabled:opacity-60"
                      />
                      <button
                        type="submit"
                        disabled={selectedTopic.is_closed}
                        className="mt-3 rounded-full bg-pixy-blue px-5 py-3 text-sm font-black text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Repondre
                      </button>
                    </form>
                  ) : (
                    <div className="mt-6 rounded-3xl border border-sky-100 bg-pixy-sky/70 p-5 text-sm text-slate-600">
                      Connectez-vous pour participer au forum.
                      <button
                        type="button"
                        onClick={() => navigate("/authentification#connexion")}
                        className="ml-0 mt-3 block rounded-full bg-pixy-blue px-5 py-3 text-sm font-black text-white sm:ml-3 sm:mt-0 sm:inline-block"
                      >
                        Connexion / Inscription
                      </button>
                    </div>
                  )}
                </article>
              )}
            </section>
          </main>
        </div>

        <section className="rounded-[36px] border border-pixy-blue/15 bg-white/85 p-6 shadow-[0_24px_80px_rgba(47,157,226,0.12)] backdrop-blur lg:p-8">
          <div className="grid gap-6 lg:grid-cols-[0.75fr_1.25fr] lg:items-start">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.3em] text-pixy-blue">
                MODÉRATION
              </p>
              <h2 className="mt-3 text-3xl font-black text-pixy-ink">
                Devenir Modérateur
              </h2>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                Tu connais bien PixyQuiz et tu veux aider la communauté ?
                Envoie une demande de modération depuis ce formulaire.
              </p>
              <div className="mt-5 rounded-3xl border border-emerald-100 bg-pixy-mist/80 p-5 text-sm leading-7 text-slate-600">
                Les demandes sont étudiées par l'équipe PixyQuiz.
              </div>
            </div>

            <div className="relative rounded-[32px] border border-sky-100 bg-pixy-sky/70 p-5">
              {(applicationError || applicationMessage) && (
                <div
                  className={`mb-4 rounded-2xl border px-4 py-3 text-sm font-semibold ${
                    applicationError
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : "border-emerald-200 bg-emerald-50 text-emerald-700"
                  }`}
                >
                  {applicationError || applicationMessage}
                </div>
              )}
              <form
                onSubmit={submitApplication}
                className={`transition ${!canUseAuthenticatedActions ? "pointer-events-none select-none blur-[3px]" : ""}`}
                aria-hidden={!canUseAuthenticatedActions}
              >
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px]">
                  <textarea
                    rows={5}
                    value={applicationMotivation}
                    onChange={(event) => setApplicationMotivation(event.target.value)}
                    placeholder="Pourquoi veux-tu aider à modérer PixyQuiz ?"
                    className="w-full resize-none rounded-2xl border border-sky-100 bg-white px-4 py-3 text-sm text-pixy-ink outline-none placeholder:text-slate-400 focus:border-pixy-blue sm:row-span-2"
                  />
                  <select
                    value={applicationCategoryId}
                    onChange={(event) => setApplicationCategoryId(event.target.value)}
                    className="w-full rounded-2xl border border-sky-100 bg-white px-4 py-3 text-sm text-pixy-ink outline-none focus:border-pixy-blue"
                  >
                    {gameCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    className="rounded-full bg-gradient-to-r from-pixy-green via-pixy-blue to-sky-500 px-5 py-3 text-sm font-black text-white shadow-sm"
                  >
                    Envoyer ma candidature
                  </button>
                </div>
              </form>

              {!canUseAuthenticatedActions ? (
                <div className="absolute inset-0 flex items-center justify-center rounded-[32px] bg-white/70 p-5 backdrop-blur-sm">
                  <div className="max-w-md rounded-3xl border border-white bg-white/95 p-6 text-center shadow-[0_20px_60px_rgba(47,157,226,0.18)]">
                    <p className="text-lg font-black text-pixy-ink">
                      Connectez-vous ou inscrivez-vous pour devenir moderateur sur PixyQuiz.
                    </p>
                    <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                      <a
                        href="/authentification#connexion"
                        className="rounded-full bg-pixy-blue px-5 py-3 text-sm font-black text-white"
                      >
                        Connexion
                      </a>
                      <a
                        href="/authentification#inscription"
                        className="rounded-full border border-pixy-blue/25 bg-white px-5 py-3 text-sm font-black text-pixy-blue"
                      >
                        Inscription
                      </a>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="rounded-[36px] border border-pixy-blue/15 bg-white/85 p-6 shadow-[0_24px_80px_rgba(47,157,226,0.12)] backdrop-blur lg:p-8">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-pixy-blue">
            VOTE COMMUNAUTÉ
          </p>
          <h2 className="mt-3 text-3xl font-black text-pixy-ink">
            Demandes de modération en cours
          </h2>
          <p className="mt-3 text-sm leading-7 text-slate-600">
            La communauté vote pendant 15 jours. Une demande est acceptée par l'équipe
            si elle atteint au moins 60% de votes "Oui".
          </p>

          {applicationsLoading && (
            <p className="mt-5 text-sm text-slate-500">Chargement des demandes...</p>
          )}
          {!applicationsLoading && applicationsError && (
            <p className="mt-5 text-sm text-rose-700">{applicationsError}</p>
          )}
          {!applicationsLoading && !applicationsError && moderatorApplications.length === 0 && (
            <p className="mt-5 text-sm text-slate-500">Aucune demande de modération en attente.</p>
          )}

          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            {moderatorApplications.map((application) => {
              const total = application.vote_total || 0;
              const yesPercent = total ? Math.round((application.upvotes / total) * 100) : 0;
              const noPercent = total ? Math.round((application.downvotes / total) * 100) : 0;
              return (
                <div key={application.id} className="rounded-3xl border border-emerald-100 bg-pixy-mist/80 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-lg font-black text-pixy-ink">{application.username}</h3>
                    <span className="text-xs font-semibold text-slate-500">
                      {application.voting_open
                        ? `Vote ouvert jusqu'au ${formatDate(application.voting_deadline)}`
                        : "Vote terminé"}
                    </span>
                  </div>
                  {application.categories?.length > 0 && (
                    <p className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                      {application.categories.map((category) => (
                        <span key={category.id} className="rounded-full border border-sky-100 bg-white px-3 py-1">
                          {category.name}
                        </span>
                      ))}
                    </p>
                  )}
                  {application.motivation && (
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                      {application.motivation}
                    </p>
                  )}
                  <div className="mt-4 text-sm font-semibold text-slate-600">
                    {total} vote{total > 1 ? "s" : ""} - {yesPercent}% Oui / {noPercent}% Non
                  </div>
                  <div className="mt-2 h-3 overflow-hidden rounded-full bg-white">
                    <div
                      className="h-full rounded-full bg-pixy-green"
                      style={{ width: `${yesPercent}%` }}
                    />
                  </div>
                  {application.has_voted && (
                    <p className="mt-3 text-xs font-bold text-pixy-green">
                      Tu as deja vote ({application.current_vote > 0 ? "Oui" : "Non"}).
                    </p>
                  )}
                  {application.voting_open && !application.has_voted && (
                    canUseAuthenticatedActions ? (
                      application.user_id === user?.id ? (
                        <p className="mt-3 text-xs text-slate-500">
                          Tu ne peux pas voter sur ta propre candidature.
                        </p>
                      ) : (
                        <div className="mt-4 flex gap-3">
                          <button
                            type="button"
                            onClick={() => submitApplicationVote(application.id, 1)}
                            className="rounded-full bg-pixy-green px-5 py-2.5 text-sm font-black text-white shadow-sm"
                          >
                            Voter Oui
                          </button>
                          <button
                            type="button"
                            onClick={() => submitApplicationVote(application.id, -1)}
                            className="rounded-full bg-pixy-blue px-5 py-2.5 text-sm font-black text-white shadow-sm"
                          >
                            Voter Non
                          </button>
                        </div>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigate("/authentification#connexion")}
                        className="mt-4 rounded-full bg-pixy-blue px-5 py-2.5 text-sm font-black text-white"
                      >
                        Connexion pour voter
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
